import { ModifierKeys } from "@/enums/keyboardShortcuts";

import type { ShortcutAction } from "@/enums/keyboardShortcuts";
import type { UserKeyboardShortcut } from "@/models/options";

export const isMacOs = window.navigator.userAgent.toLowerCase().includes("mac");
export const metaKey = isMacOs ? "⌘" : "CTRL";

type ConversionMap = {
  // underscore-joined identifier for API/storage (e.g. "ctrl_d")
  action_string?: string;
  modifiers?: ModifierKeys[];
  keys?: string[];
};

export const WINDOWS_CONVERSION_MAP: Record<string, ConversionMap> = {
  // required only for default shortcuts as customized shortcuts will send the correct values
  cmd_shift_d: {
    action_string: "ctrl_alt_d",
    modifiers: [ModifierKeys.Ctrl, ModifierKeys.Alt],
  },
  cmd_shift_a: {
    action_string: "ctrl_alt_a",
    modifiers: [ModifierKeys.Ctrl, ModifierKeys.Alt],
  },
  cmd_shift_i: {
    action_string: "ctrl_alt_i",
    modifiers: [ModifierKeys.Ctrl, ModifierKeys.Alt],
  },
  cmd_backslash: {
    action_string: "ctrl_shift_backslash",
    modifiers: [ModifierKeys.Ctrl, ModifierKeys.Shift],
  },
  alt_left: {
    action_string: "alt_up",
    keys: ["ArrowUp"],
  },
  alt_right: {
    action_string: "alt_down",
    keys: ["ArrowDown"],
  },
  alt_ß: {
    action_string: "alt_s",
    keys: ["s"],
  },
  alt_ƒ: {
    action_string: "alt_f",
    keys: ["f"],
  },
  "alt_˚": {
    action_string: "alt_k",
    keys: ["k"],
  },
  "alt_∆": {
    action_string: "alt_j",
    keys: ["j"],
  },
  alt_ç: {
    action_string: "alt_c",
    keys: ["c"],
  },
  "alt_shift_´": {
    action_string: "alt_shift_e",
    keys: ["e"],
  },
  "alt_†": {
    action_string: "alt_t",
    keys: ["t"],
  },
  "alt_≤": {
    action_string: "alt_,",
    keys: [","],
  },
  "alt_≥": {
    action_string: "alt_.",
    keys: ["."],
  },
  "alt_∂": {
    action_string: "alt_d",
    keys: ["d"],
  },
  alt_π: {
    action_string: "alt_p",
    keys: ["p"],
  },
  alt_å: {
    action_string: "alt_a",
    keys: ["a"],
  },
  "alt_¬": {
    action_string: "alt_l",
    keys: ["l"],
  },
  "alt_®": {
    action_string: "alt_r",
    keys: ["r"],
  },
  "alt_shift_˜": {
    action_string: "alt_shift_n",
    keys: ["n"],
  },
  "alt_shift_¨": {
    action_string: "alt_shift_u",
    keys: ["u"],
  },
  alt_shift_slash: {
    keys: ["?"],
  },
  alt_ω: {
    action_string: "alt_z",
    keys: ["z"],
  },
  alt_shift_Â: {
    action_string: "alt_shift_m",
    keys: ["m"],
  },
  alt_shift_Î: {
    action_string: "alt_shift_d",
    keys: ["d"],
  },
  "alt_¡": {
    action_string: "alt_1",
    keys: ["1"],
  },
  "alt_™": {
    action_string: "alt_2",
    keys: ["2"],
  },
  "alt_£": {
    action_string: "alt_3",
    keys: ["3"],
  },
  "alt_¢": {
    action_string: "alt_4",
    keys: ["4"],
  },
  "alt_∞": {
    action_string: "alt_5",
    keys: ["5"],
  },
  "alt_§": {
    action_string: "alt_6",
    keys: ["6"],
  },
  "alt_¶": {
    action_string: "alt_7",
    keys: ["7"],
  },
  cmd_shift_n: {
    action_string: "ctrl_shift_n",
    modifiers: [ModifierKeys.Ctrl, ModifierKeys.Shift],
  },
  cmd_shift_e: {
    action_string: "ctrl_e",
    modifiers: [ModifierKeys.Ctrl],
  },
  cmd_shift_b: {
    action_string: "ctrl_alt_b",
    modifiers: [ModifierKeys.Ctrl, ModifierKeys.Alt],
  },
  cmd_arrowup: {
    action_string: "ctrl_arrowup",
    modifiers: [ModifierKeys.Ctrl],
  },
  cmd_arrowdown: {
    action_string: "ctrl_arrowdown",
    modifiers: [ModifierKeys.Ctrl],
  },
};

export const convertToWindows = (
  shortcuts: UserKeyboardShortcut[]
): UserKeyboardShortcut[] => {
  return shortcuts.map((sc) => {
    const conversion =
      WINDOWS_CONVERSION_MAP[
        sc.action_string as keyof typeof WINDOWS_CONVERSION_MAP
      ];

    const {
      action_string: newActionString,
      modifiers: newModifiers,
      keys: newKeys,
    } = conversion ?? {};
    const {
      action_string: oldActionString,
      modifiers: oldModifiers,
      keys: oldKeys,
    } = sc;

    const actionString = (newActionString ?? oldActionString).replace(
      "cmd",
      "ctrl"
    );
    const modifiers =
      (newModifiers ?? oldModifiers ?? []).map((modifier) =>
        modifier === ModifierKeys.Meta ? ModifierKeys.Ctrl : modifier
      ) ?? [];
    const keys = newKeys ?? oldKeys;

    return {
      ...sc,
      action_string: (newActionString ?? actionString) as ShortcutAction,
      modifiers,
      keys,
    };
  });
};
